import React, { useState, useEffect } from 'react';
import { X, Search, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';
import './HotSearchModal.css';
import { recordActionLog } from '../../../utils/logger';

const HotSearchModal = ({ isOpen, onClose }) => {
  const [platforms, setPlatforms] = useState({});
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [activePlatform, setActivePlatform] = useState('');
  const [hotList, setHotList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPlatforms();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && activePlatform) {
      fetchList(activePlatform);
    }
  }, [activePlatform, isOpen]);

  const fetchPlatforms = async () => {
    try {
      const res = await fetch('/api/v1/hotsearch/platforms').then(r => r.json());
      if (res.code === 200 && res.data) {
        setPlatforms(res.data);
        const cats = Object.keys(res.data);
        setCategories(cats);
        if (cats.length > 0 && !activeCategory) {
          setActiveCategory(cats[0]);
          if (res.data[cats[0]]?.length > 0) {
            setActivePlatform(res.data[cats[0]][0]);
          }
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchList = async (platformName) => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/hotsearch/list').then(r => r.json());
      if (res.code === 200 && res.data) {
        const allData = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        setHotList(allData[platformName] || []);
      }
    } catch (e) {
      console.error(e);
      setHotList([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentPlatforms = platforms[activeCategory] || [];

  // Helper to generate a colored circle based on platform name
  const getPlatformIcon = (name) => {
    if (!name || typeof name !== 'string') return null;
    const char = name.charAt(0);
    let bgColor = '#1677ff';
    if (name.includes('微博')) bgColor = '#ff4d4f';
    if (name.includes('抖音')) bgColor = '#252632';
    if (name.includes('知乎')) bgColor = '#0066ff';
    if (name.includes('B站') || name.includes('哔哩')) bgColor = '#fb7299';
    if (name.includes('头条')) bgColor = '#f04142';
    
    return (
      <div className="hotsearch-platform-icon" style={{ backgroundColor: bgColor }}>
        {char}
      </div>
    );
  };

  return (
    <div className="hotsearch-overlay" onClick={onClose} style={{ padding: isFullScreen ? 0 : '', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)' }}>
      <div className={`hotsearch-modal ${isFullScreen ? 'fullscreen' : ''}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="hotsearch-header">
          <div className="hotsearch-header-left">
            <div className="hotsearch-logo">
              <Search size={20} color="#fff" strokeWidth={2.5} />
            </div>
            <div className="hotsearch-titles">
              <h2>热搜榜</h2>
              <p>聚合多平台热搜，快速掌握最新动态</p>
            </div>
          </div>
          <div className="hotsearch-header-right">
            <div className="hotsearch-window-controls">
              <button className="hotsearch-btn-close" onClick={() => setIsFullScreen(!isFullScreen)}>
                {isFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button className="hotsearch-btn-close" onClick={onClose}>
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Body */}
        <div className="hotsearch-body">
          {/* Sidebar */}
          <div className="hotsearch-sidebar">
            <div className="hotsearch-sidebar-list">
              {currentPlatforms.map(plat => (
                <div 
                  key={plat} 
                  className={`hotsearch-sidebar-item ${activePlatform === plat ? 'active' : ''}`}
                  onClick={() => setActivePlatform(plat)}
                >
                  <div className="hotsearch-sidebar-indicator"></div>
                  {getPlatformIcon(plat)}
                  <span className="hotsearch-sidebar-name">{plat}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Main Content */}
          <div className="hotsearch-content">
            {/* Tabs */}
            <div className="hotsearch-tabs-container">
              <div className="hotsearch-tabs">
                {categories.map(cat => (
                  <div 
                    key={cat} 
                    className={`hotsearch-pill-tab ${activeCategory === cat ? 'active' : ''}`}
                    onClick={() => {
                      setActiveCategory(cat);
                      if (platforms[cat]?.length > 0) setActivePlatform(platforms[cat][0]);
                    }}
                  >
                    {cat}
                  </div>
                ))}
              </div>
            </div>

            {/* List Header */}
            <div className="hotsearch-list-header">
              <div className="hotsearch-list-title">
                {getPlatformIcon(activePlatform)}
                <h3>{activePlatform} · 实时热点</h3>
              </div>
            </div>

            {/* List */}
            <div className="hotsearch-list-wrapper">
              {loading && hotList.length === 0 ? (
                <div className="hotsearch-loading">加载中...</div>
              ) : hotList.length === 0 ? (
                <div className="hotsearch-empty">暂无数据</div>
              ) : (
                <div className="hotsearch-list">
                  {hotList.map((item, index) => (
                    <a 
                      key={item.id || index} 
                      href={item.url} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="hotsearch-list-item"
                      onClick={() => {
                        recordActionLog('click_hot_search', 'hot_search_widget', item.title, { platform: activePlatform, url: item.url, name: item.title }, item.title);
                      }}
                    >
                      <div className={`hotsearch-rank rank-${index + 1}`}>{index + 1}</div>
                      <div className="hotsearch-item-title">{item.title}</div>
                      <div className="hotsearch-item-hot">{item.hotIndex}</div>
                    </a>
                  ))}
                  <div className="hotsearch-footer-note">
                    热搜数据来自公开平台，实时聚合展示
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotSearchModal;
